import { axiosReq } from "@/lib/axios";
import { queryClient } from "@/lib/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";

export const useGetSingleImage = (id: string | undefined) => {

  const request = async (): Promise<{
    image_id: number,
    image_url: string,
    prompt: string,
    type: string
  }> => {
    const response = await axiosReq.post('/api/asset/get-single-image/',
      { image_id: id ? parseInt(id) : 0 });
    return response.data
  }

  const { data: imageData } = useQuery({
    queryKey: ["single-image", id],
    queryFn: request,
    enabled: !!id
  })

  return { imageData }
}

export const useEditImageMask = (onMaskComplete: (url: string, canUndo: boolean) => void) => {

  const request = async (
    { prompt, mask, image_id }:
      { prompt: string, mask: string, image_id: number })
    : Promise<{ image_id: number, new_url: string }> => {
    const response = await axiosReq.post('/api/asset/edit-image/', {
      image_id,
      prompt,
      mask
    })

    return response.data
  }

  const { mutate: editImageMask, status } = useMutation({
    mutationFn: request,
    onSuccess: (data) => {
      if (data) {
        onMaskComplete(data.new_url, true)
      }
    }
  })

  return { editImageMask, status }
}

export const useUndoEditImage = (onUndo: (image_id: number ,url: string,  canUndo?: boolean) => void) => {

  const request = async(id: number):Promise<{
  image_id: number,
  reverted_url: string
  }> =>{
    const response = await axiosReq.post('/api/asset/undo-edit-image/', {
      image_id: id
    })

    return response.data;
  }

  const { mutate: undoImage, status: undoStatus, data: lastSavedImage } = useMutation({
    mutationFn: request,
    onSuccess: (data) =>{
      queryClient.invalidateQueries({queryKey: ["single-image"]})
      queryClient.invalidateQueries({queryKey: ["theme-details"]})
      onUndo(data.image_id ,data.reverted_url, false)
    }
  })

  return { undoImage, undoStatus, lastSavedImage }
}


type UploadTemplateArgs = { event_id: number, image_id: number, image: File }

export const useSaveImageTemplate = () => {

  const config = {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }

  const request = async (data: UploadTemplateArgs) => {
    const response = await axiosReq.post('/api/asset/save-template-image/', data, config)
    return response.data;
  }

  const { mutateAsync: uploadTemplate } = useMutation({
    mutationFn: request
  })

  return { uploadTemplate };
}

export const useSaveImageEdit = () =>{

  const request = async({image_id, image_file}: { image_id: number, image_file: string }) => {
    const response = await axiosReq.post('/api/asset/save-image/', { image_id, mask: image_file })

    return response.data
  }

  const { mutate: saveImageEdit, status } = useMutation({
    mutationFn: request,
    onSuccess: () => {
      // refresh theme details data, scheduled posts
      queryClient.invalidateQueries({queryKey: ["gallery-catalogue"]})
      queryClient.invalidateQueries({queryKey: ["gallery"]})
      queryClient.invalidateQueries({queryKey: ["wishlist"]})
      queryClient.invalidateQueries({queryKey: ["theme-details"]})
    }
  })

  return { saveImageEdit, status }
}
